/**
 * Export.
 *
 * The complete, print-ready document for every plan. What is left to decide is
 * how it prints — how much of each guest a chair carries, and how many sheets a
 * big room spans — and the dialog says what each choice costs in paper before
 * it is made.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'

import { whenSaved } from '../hooks/useAutosave'
import { api } from '../lib/api'
import { overlappingPairs } from '../lib/collision'
import { useT } from '../lib/i18n/I18nProvider'
import { messageForError } from '../lib/i18n/errors'
import type { Plan, SeatLabelMode } from '../lib/types'
import { usePlanStore } from '../store/planStore'

interface Props {
  open: boolean
  onClose: () => void
  plan: Plan
  /** Floor-plan pages the renderer would choose on its own (1, 2 or 4). */
  floorPlanPages: number
  /** What each chair style costs in paper, so the picker can say so up front. */
  pagesBySeatLabels: Record<SeatLabelMode, number> | null
}

export function ExportDialog({
  open,
  onClose,
  plan,
  floorPlanPages,
  pagesBySeatLabels,
}: Props) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The floor-plan page choice. `null` means "whatever the room recommends",
  // so a recommendation that arrives after the dialog opened still lands.
  const [pageChoice, setPageChoice] = useState<number | null>(null)
  // The chair style for *this* download. Starts from the plan's own setting;
  // overruling it here never edits the plan, the same way the page count does not.
  const [labelChoice, setLabelChoice] = useState<SeatLabelMode | null>(null)
  const showToast = usePlanStore((s) => s.showToast)

  const seatLabels = labelChoice ?? plan.settings.seatLabels
  // Name plates need more paper than discs, so switching style moves the
  // recommendation with it.
  const recommendedPages = pagesBySeatLabels?.[seatLabels] ?? floorPlanPages
  const floorPages = pageChoice ?? recommendedPages
  // Only rooms big enough to split get the choice at all — a five-table plan
  // shouldn't ask a question whose answer is obvious.
  const showPagePicker = useMemo(() => recommendedPages > 1, [recommendedPages])

  useEffect(() => {
    if (!open) return
    // Each visit starts from the plan's own settings.
    setPageChoice(null)
    setLabelChoice(null)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // The print is honest: stacked furniture prints stacked. Saying so here —
  // with names, before any download — is the last line of defence after the
  // canvas has already nudged fresh drops apart; old plans and jammed rooms
  // can still hold an overlap.
  const overlaps = useMemo(() => overlappingPairs(plan.tables), [plan.tables])

  const seated = plan.tables.reduce(
    (sum, table) => sum + table.seats.filter((seat) => seat.guestId).length,
    0,
  )
  const download = async () => {
    setError(null)
    setBusy(true)
    try {
      // The PDF is rendered from what is stored, so let a pending save land first.
      await whenSaved()
      await api.downloadPdf(plan.id, { floorPages, seatLabels })
      showToast(t.exportDialog.pdfReady, 'success')
      onClose()
    } catch (err) {
      setError(messageForError(t, err, t.errors.exportFailed))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            className="modal"
            initial={{ opacity: 0, scale: 0.94, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-title"
          >
            <button type="button" className="modal-close" onClick={onClose} aria-label={t.common.close}>
              ×
            </button>

            <span className="modal-eyebrow">{t.exportDialog.eyebrow}</span>
            <h2 id="export-title" className="modal-title">
              {t.exportDialog.readyTitle}
            </h2>
            <p className="modal-lede">{t.exportDialog.readyLede}</p>
            <PdfContents seated={seated} tables={plan.tables.length} />
            <SeatLabelPicker
              value={seatLabels}
              pages={pagesBySeatLabels}
              onChange={(mode) => {
                setLabelChoice(mode)
                setPageChoice(null)
              }}
            />
            {showPagePicker && (
              <FloorPagesPicker
                value={floorPages}
                recommended={recommendedPages}
                onChange={setPageChoice}
              />
            )}
            <OverlapNote overlaps={overlaps} />
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary modal-cta"
                onClick={() => void download()}
                disabled={busy}
              >
                {busy ? t.exportDialog.preparing : t.exportDialog.downloadPdf}
              </button>
            </div>

            {error && <p className="modal-error">{error}</p>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * How many pages the floor plan spans: 1, 2 or 4 — never more.
 *
 * One tap, preselected to what fits the room, shown only when the room is big
 * enough for the answer to be non-obvious. "Larger tables across more pages"
 * says everything the planner needs to weigh; anything longer would be a
 * settings screen inside a download.
 */
function FloorPagesPicker({
  value,
  recommended,
  onChange,
}: {
  value: number
  recommended: number
  onChange: (pages: number) => void
}) {
  const t = useT()
  return (
    <div className="floor-pages">
      <span className="floor-pages-label">
        <strong>{t.exportDialog.floorPlan}</strong>
        <small>{t.exportDialog.floorPlanBlurb}</small>
      </span>
      <div className="floor-pages-pills" role="radiogroup" aria-label={t.exportDialog.floorPlanPages}>
        {[1, 2, 4].map((pages) => (
          <button
            key={pages}
            type="button"
            role="radio"
            aria-checked={value === pages}
            className={`floor-pages-pill${value === pages ? ' is-active' : ''}`}
            onClick={() => onChange(pages)}
          >
            {t.exportDialog.pages(pages)}
            {pages === recommended && (
              <span className="floor-pages-fit">{t.exportDialog.fitsBest}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * How chairs are drawn on the printed floor plan.
 *
 * The page cost is shown against each option rather than discovered after the
 * download: whole names need roughly twice the room a disc does, so on a big
 * plan this choice is also a choice about how many sheets come out of the
 * printer, and hiding that would make the page picker below look broken.
 */
function SeatLabelPicker({
  value,
  pages,
  onChange,
}: {
  value: SeatLabelMode
  pages: Record<SeatLabelMode, number> | null
  onChange: (mode: SeatLabelMode) => void
}) {
  const t = useT()
  const modes: SeatLabelMode[] = ['name', 'initials', 'none']
  return (
    <div className="floor-pages">
      <span className="floor-pages-label">
        <strong>{t.exportDialog.chairs}</strong>
        <small>{t.exportDialog.chairsBlurb}</small>
      </span>
      <div className="floor-pages-pills" role="radiogroup" aria-label={t.exportDialog.chairs}>
        {modes.map((mode) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={value === mode}
            className={`floor-pages-pill${value === mode ? ' is-active' : ''}`}
            onClick={() => onChange(mode)}
          >
            {t.tableSettings.seatLabel[mode]}
            {pages && pages[mode] > 1 && (
              <span className="floor-pages-fit">{t.exportDialog.pages(pages[mode])}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Tables that will print on top of each other, named. Client-side geometry —
 * the same module the canvas settles drops with — so the list is exactly what
 * the floor plan will show.
 */
function OverlapNote({ overlaps }: { overlaps: [{ name: string }, { name: string }][] }) {
  const t = useT()
  if (overlaps.length === 0) return null
  return (
    <div className="overlap-note" role="alert">
      <strong>{t.exportDialog.overlapWarning(overlaps.length)}</strong>
      <small>{overlaps.map(([a, b]) => `${a.name} ↔ ${b.name}`).join(' · ')}</small>
      <small>{t.exportDialog.overlapBlurb}</small>
    </div>
  )
}

function PdfContents({ seated, tables }: { seated: number; tables: number }) {
  const t = useT()
  const items = [
    { title: t.exportDialog.contents.floorPlan, detail: t.exportDialog.contents.floorPlanDetail(tables) },
    { title: t.exportDialog.contents.guestCards, detail: t.exportDialog.contents.guestCardsDetail(seated) },
    { title: t.exportDialog.contents.findYourSeat, detail: t.exportDialog.contents.findYourSeatDetail },
  ]
  return (
    <ul className="pdf-contents">
      {items.map((item, index) => (
        <motion.li
          key={item.title}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.06 + index * 0.05 }}
        >
          <span className="pdf-page-icon">{index + 1}</span>
          <span>
            <strong>{item.title}</strong>
            <small>{item.detail}</small>
          </span>
        </motion.li>
      ))}
    </ul>
  )
}
